import React, { useState, useEffect, useCallback } from 'react';
import { History, Save, RotateCcw, ChevronRight, Clock, MessageSquare, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const VersionHistory = ({ project }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [snapshotLabel, setSnapshotLabel] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);

  const fetchSnapshots = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await api.get(`/projects/${project.id}/snapshots`);
      setSnapshots(res.data.snapshots || []);
    } catch { setSnapshots([]); }
    setLoading(false);
  }, [project?.id]);

  useEffect(() => { fetchSnapshots(); }, [fetchSnapshots]);

  const handleSave = async () => {
    if (!project?.id) return;
    setSaving(true);
    try {
      const res = await api.post(`/projects/${project.id}/snapshots`, {
        label: snapshotLabel.trim() || undefined
      });
      if (res.data.success) {
        toast({ title: 'Snapshot Saved', description: `"${res.data.label}" — ${res.data.message_count} messages` });
        setSnapshotLabel('');
        fetchSnapshots();
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save snapshot', variant: 'destructive' });
    }
    setSaving(false);
  };

  const handleRestore = async (snapshotId, label) => {
    if (!project?.id) return;
    setRestoring(snapshotId);
    try {
      const res = await api.post(`/projects/${project.id}/snapshots/${snapshotId}/restore`);
      if (res.data.success) {
        toast({ title: 'Restored!', description: `Rolled back to "${label}". Auto-backup created.` });
        fetchSnapshots();
      }
    } catch (err) {
      toast({ title: 'Restore Failed', description: err.response?.data?.detail || 'Failed to restore', variant: 'destructive' });
    }
    setRestoring(null);
  };

  const handleExpand = async (snapshotId) => {
    if (expandedId === snapshotId) { setExpandedId(null); setExpandedData(null); return; }
    setExpandedId(snapshotId);
    try {
      const res = await api.get(`/projects/${project.id}/snapshots/${snapshotId}`);
      setExpandedData(res.data);
    } catch { setExpandedData(null); }
  };

  return (
    <div className="h-full flex flex-col bg-background overflow-y-auto">
      {/* Header */}
      <div className="border-b border-border p-5 bg-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center">
            <History className="w-5 h-5 text-foreground" />
          </div>
          <div>
            <h2 className="text-foreground font-semibold text-lg" data-testid="version-title">Version History</h2>
            <p className="text-xs text-muted-foreground">Save and restore conversation snapshots</p>
          </div>
        </div>

        {/* Create Snapshot */}
        <div className="flex gap-2">
          <Input
            value={snapshotLabel}
            onChange={e => setSnapshotLabel(e.target.value)}
            placeholder="Snapshot label (optional)"
            className="bg-background border-input text-foreground placeholder:text-muted-foreground text-sm"
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            data-testid="snapshot-label-input"
          />
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 gap-1.5 shrink-0" data-testid="save-snapshot-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Snapshots List */}
      <div className="flex-1 p-5 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="animate-pulse p-4 rounded-lg bg-muted">
                <div className="h-4 bg-[#222222] rounded w-2/3 mb-2" />
                <div className="h-3 bg-secondary rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : snapshots.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <History className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No snapshots yet</p>
            <p className="text-xs mt-1 text-muted-foreground/70">Save a snapshot to create a restore point</p>
          </div>
        ) : (
          snapshots.map(snap => (
            <Card key={snap.id} className="bg-card border-border hover:border-input transition-all" data-testid={`snapshot-${snap.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-foreground font-medium text-sm truncate">{snap.label}</h3>
                      {snap.is_auto_backup && (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs gap-1 shrink-0">
                          <Shield className="w-2.5 h-2.5" /> Auto-backup
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {snap.message_count} messages</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(snap.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-2">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleExpand(snap.id)}
                      className="h-7 px-2 border-input text-muted-foreground hover:text-foreground hover:bg-accent"
                      data-testid={`snapshot-expand-${snap.id}`}
                    >
                      <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expandedId === snap.id ? 'rotate-90' : ''}`} />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleRestore(snap.id, snap.label)}
                      disabled={restoring === snap.id}
                      className="h-7 px-2.5 bg-amber-600 hover:bg-amber-500 text-white gap-1 text-xs"
                      data-testid={`snapshot-restore-${snap.id}`}
                    >
                      <RotateCcw className={`w-3 h-3 ${restoring === snap.id ? 'animate-spin' : ''}`} /> Restore
                    </Button>
                  </div>
                </div>

                {/* Expanded preview */}
                {expandedId === snap.id && expandedData && (
                  <div className="mt-3 pt-3 border-t border-border max-h-[200px] overflow-y-auto space-y-2">
                    {expandedData.messages?.slice(-5).map((msg, i) => (
                      <div key={i} className={`text-xs p-2 rounded ${msg.role === 'user' ? 'bg-blue-500/10 text-blue-300' : 'bg-secondary text-muted-foreground'}`}>
                        <span className="font-medium text-muted-foreground mr-1">{msg.role === 'user' ? 'You:' : 'AI:'}</span>
                        {(msg.content || '').substring(0, 150)}{(msg.content || '').length > 150 ? '...' : ''}
                      </div>
                    ))}
                    {expandedData.messages?.length > 5 && (
                      <p className="text-xs text-muted-foreground/70 text-center">...and {expandedData.messages.length - 5} more messages</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default VersionHistory;
